import React from 'react';
import { Box, Typography, Chip, Button } from '@mui/material';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { MyDashboardService } from '../../../../services/my-dashboard.service';

const BLUE = '#206FA5';
const ORANGE = '#E65100';

const ROLE_META: Record<string, { label: string; description: string; showItems: boolean }> = {
    POM_MASTER: { label: 'POM Master', description: 'Viewing all data across this organization', showItems: false },
    SUPER_ADMIN: { label: 'Admin', description: 'Viewing all data across your organization', showItems: false },
    ADMIN: { label: 'Admin', description: 'Viewing all data across your organization', showItems: false },
    VIEW_ONLY: { label: 'View Only', description: 'Viewing all data across your organization', showItems: false },
    GROUP_LEADER: { label: 'Group Leader', description: 'Showing data for your groups', showItems: true },
    TEAM_LEADER: { label: 'Team Leader', description: 'Showing data for your teams', showItems: true },
};

const DEFAULT_META = { label: 'User', description: 'Viewing your assigned data', showItems: false };

export const ScopeBanner: React.FC = () => {
    const scope = useSelector((s: RootState) => s.myDashboard.scope);
    const loading = useSelector((s: RootState) => s.myDashboard.loading.scope);
    const scopeError = useSelector((s: RootState) => s.myDashboard.scopeError);

    if (loading && !scope) return null;

    // When the scope fetch fails, GroupLeader/TeamLeader users would otherwise
    // see no banner and no indication their data is scoped — render a retry
    // affordance so the failure is observable and recoverable.
    if (!scope && scopeError) {
        return (
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                mb: 2, px: 2, py: 1,
                bgcolor: `${ORANGE}0D`, borderRadius: '8px',
                border: `1px solid ${ORANGE}30`,
            }}>
                <WifiOffIcon sx={{ fontSize: 16, color: ORANGE }} />
                <Typography sx={{ fontSize: 12, color: '#64748B', flex: 1 }}>
                    Couldn’t load your data scope. If you lead groups or teams, the scope chips below won’t appear until this reconnects.
                </Typography>
                <Button
                    size="small"
                    onClick={() => MyDashboardService.fetchScope()}
                    sx={{ fontSize: 11, fontWeight: 700, color: ORANGE, textTransform: 'none' }}
                >
                    Retry
                </Button>
            </Box>
        );
    }

    if (!scope) return null;

    const meta = ROLE_META[scope.role] || DEFAULT_META;
    const items = meta.showItems
        ? (scope.role === 'GROUP_LEADER' ? scope.groups : scope.teams)
        : [];

    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            mb: 2, px: 2, py: 1,
            bgcolor: `${BLUE}08`, borderRadius: '8px',
            border: `1px solid ${BLUE}15`,
        }}>
            <Chip
                label={meta.label}
                size="small"
                sx={{
                    fontSize: 10, fontWeight: 700, height: 22,
                    bgcolor: `${BLUE}15`, color: BLUE,
                    border: `1px solid ${BLUE}30`,
                }}
            />
            <Typography sx={{ fontSize: 12, color: '#64748B' }}>
                {meta.description}
            </Typography>
            {items.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {items.map((item) => (
                        <Chip
                            key={item.id}
                            label={item.name}
                            size="small"
                            sx={{
                                // Matches UnifiedFilterBar applied-filter pill style
                                height: 22,
                                fontSize: 11, fontWeight: 500,
                                bgcolor: '#DBEAFE', color: '#1E40AF',
                                borderRadius: '10px',
                                px: 0.25,
                                '& .MuiChip-label': { px: 1 },
                            }}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};
